import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'sonner';
import { useBenefitsStore } from 'Application/store';
import { PlanBenefitsType, SectionOFCover } from 'Application/models/enums';
import { ThemeMode } from 'config';

interface AddNewBenefit {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const AddBenefit: React.FC<AddNewBenefit> = ({ isOpen, setIsOpen }) => {
    const { createBenefit, isLoading } = useBenefitsStore();

    const [formData, setFormData] = useState({
        benefitName: '',
        sectionOfCover: '' as SectionOFCover,
        type: '' as PlanBenefitsType
    });

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    const handleChange = (e: SelectChangeEvent) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            await createBenefit(formData);
            toast.success('Benefit created successfully');
            setIsOpen(false);

            // Clear the form data after successful creation
            setFormData({
                benefitName: '',
                sectionOfCover: '' as SectionOFCover,
                type: '' as PlanBenefitsType
            });
        } catch (error) {
            toast.error(`Failed to create benefit: ${error}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Create a new Benefit</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Benefit Name
                        </Typography>
                        <TextField
                            placeholder="Enter Benefit Name"
                            required
                            name="benefitName"
                            variant="outlined"
                            value={formData.benefitName}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Type of Benefit
                        </Typography>
                        <Select name="type" value={formData.type} onChange={handleChange} fullWidth displayEmpty>
                            <MenuItem value="" disabled>
                                Select type of benefit
                            </MenuItem>
                            <MenuItem value={PlanBenefitsType.OPTIONAL}>Optional</MenuItem>
                            <MenuItem value={PlanBenefitsType.MANDATORY}>Mandatory</MenuItem>
                        </Select>
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Section of Cover
                        </Typography>
                        <Select name="sectionOfCover" value={formData.sectionOfCover} onChange={handleChange} fullWidth displayEmpty>
                            <MenuItem value="" disabled>
                                Select section of cover
                            </MenuItem>
                            <MenuItem value={SectionOFCover.A}>A</MenuItem>
                            <MenuItem value={SectionOFCover.B}>B</MenuItem>
                            <MenuItem value={SectionOFCover.C}>C</MenuItem>
                            <MenuItem value={SectionOFCover.D}>D</MenuItem>
                            <MenuItem value={SectionOFCover.E}>E</MenuItem>
                            <MenuItem value={SectionOFCover.F}>F</MenuItem>
                            <MenuItem value={SectionOFCover.G}>G</MenuItem>
                            <MenuItem value={SectionOFCover.H}>H</MenuItem>
                            <MenuItem value={SectionOFCover.I}>I</MenuItem>
                        </Select>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddBenefit;
